import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  Typography,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import FieldRichText from "components/FieldRichText";
import SelectEntity from "components/SelectEntity";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useCallback, useMemo, useState } from "react";
import { FieldInputProps, FieldMetaState, withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  AttributeMetaValue,
  OrganizationAttributeType,
  OrganizationAttributeTypeRequest,
  SaveResponse,
  SkuAttributeByType,
  SkuAttributeRequest,
} from "services/KnowfullyAdminClient";
import tinymce from "tinymce";
import {
  AttributeCodeConstants,
  AttributeDisplayTypes,
  AttributeMetaValues as AttributeMetaValueNames,
  SnackbarMessageProps,
} from "types/common-types";
import { SkuFormProps } from "types/sku-related-types";
import translateError from "utils/translateError";
import { SkuAttributeFormComponent } from "./SkuAttributeFormComponent";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { getUtcDate } from "utils/dateUtils";
import { validateSkuAttributeDates } from "utils/validationUtils";
import { SponsorTypeField, sponsorTypeIdToName } from "./SponsorTypeField";

const styles = {
  feedback: {
    width: "100%",
    margin: "20px 0 0 15px",
  },
};

type SkuAttributeFormProps = {
  skuAttributes: SkuAttributeByType[];
  organizationAttributeType?: OrganizationAttributeType;
  onCancel?: () => void;
  setSnackbarMessage: (snackbarMessage: SnackbarMessageProps | null) => void;
  onAttributeCreated: (saveResponse: SaveResponse) => void;
};

type SkuAttributeFormValues = {
  attributeId?: string;
  value: string;
  displayText?: string;
  organizationAttributeTypeId?: string;
  sequence?: number;
  attributeMetaValues?: AttributeMetaValue[];
  startDate?: Date | null;
  endDate?: Date | null;
  sponsorTypeId?: string;
};

const { Form } = withTypes<SkuAttributeFormValues>();

export default function SkuAttributeForm({
  sku,
  skuAttributes,
  organizationAttributeType: organizationAttributeTypeProp,
  onCancel,
  setSnackbarMessage,
  onAttributeCreated,
  onSkuUpdated,
  ...rest
}: SkuAttributeFormProps & SkuFormProps) {
  const queryClient = useQueryClient();
  const { formatMessage: t } = useIntl();
  const { registerPromise } = useTrashablePromises();
  const { attributeService, skuService } = useGlobalContext();
  const [organizationAttributeType, setOrganizationAttributeType] = useState(
    organizationAttributeTypeProp
  );

  const init = useMemo<SkuAttributeFormValues>(
    () => ({
      organizationAttributeTypeId: organizationAttributeTypeProp?.id ?? "",
      attributeId: "",
      value: "",
      displayText: "",
      attributeMetaValues: [],
      startDate: null,
      endDate: null,
    }),
    [organizationAttributeTypeProp]
  );

  const request = OrganizationAttributeTypeRequest.fromJS({
    sortField: "name",
    organizationId: sku.ownerOrganizationId,
    parameters: {
      attributeDisplayTypeId: AttributeDisplayTypes.AttributeSection,
    },
  });

  const fetchedOrganizationAttributeTypes = useQuery(
    ["getOrganizationAttributeTypes", request],
    () => attributeService.getOrganizationAttributeTypes(request),
    { enabled: !organizationAttributeType }
  );

  const setInnerOrganizationAttributeType = useCallback(
    (id?: string) => {
      let targetAttributeType = fetchedOrganizationAttributeTypes.data?.data?.find(
        (t) => t.id === id
      );
      setOrganizationAttributeType(targetAttributeType);
    },
    [fetchedOrganizationAttributeTypes.data?.data]
  );

  const validate = (values: SkuAttributeFormValues) => {
    const errors: Partial<Record<keyof SkuAttributeFormValues, string>> = {};

    const dateError = validateSkuAttributeDates(
      attributeType?.allowDates,
      values.startDate,
      values.endDate,
      t({ id: "endDateMustBeAfterStartDate" })
    );

    if (dateError) {
      errors.endDate = dateError;
    }

    return errors;
  };

  const onSubmit = async (values: SkuAttributeFormValues) => {
    const transformedValues: SkuAttributeFormValues = {
      ...values,
      // StartDate to 00:00:00 UTC, EndDate to 23:59:59 UTC
      startDate: getUtcDate(values.startDate ?? undefined),
      endDate: getUtcDate(values.endDate ?? undefined, {
        endOfDay: true,
      }),
    };

    if (!organizationAttributeType?.attributeType?.isReusable) {
      const displayText = tinymce.activeEditor.getContent({ format: "text" });

      if (!displayText) {
        return { [FORM_ERROR]: t({ id: "skuAttributeEmpty" }) };
      }

      transformedValues.attributeId = undefined;
      transformedValues.displayText = displayText;
    }

    const isSponsor =
      organizationAttributeType?.attributeType?.codeConstant?.toLowerCase() ===
      AttributeCodeConstants.Sponsors;
    const skuAttributeMetaValues: AttributeMetaValue[] = isSponsor
      ? [
          AttributeMetaValue.fromJS({
            name: AttributeMetaValueNames.SponsorTypeId,
            value: values.sponsorTypeId ?? "",
          }),
          AttributeMetaValue.fromJS({
            name: AttributeMetaValueNames.SponsorType,
            value: sponsorTypeIdToName(values.sponsorTypeId),
          }),
        ]
      : [];

    try {
      const response = await registerPromise(
        skuService.saveSkuAttribute(
          sku.id!,
          SkuAttributeRequest.fromJS({
            ...transformedValues,
            skuAttributeMetaValues,
          })
        )
      );

      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "skuAttributeCreated" }))
      );
      onAttributeCreated(response!);
      onSkuUpdated && onSkuUpdated(sku.id!);
      queryClient.invalidateQueries(["attributes"]);
      queryClient.invalidateQueries(["skuAttributes"]);
      queryClient.invalidateQueries(["skuAttributesByType"]);

      return response;
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  const TinyMCEComponent = (
    input: FieldInputProps<any, HTMLElement>,
    meta: FieldMetaState<any>
  ) => {
    return (
      <Grid item xs={12}>
        <FieldRichText
          input={input}
          meta={meta}
          init={{
            height: 280,
          }}
        />
      </Grid>
    );
  };

  const attributeType = organizationAttributeType?.attributeType;
  const canAddSkuAttribute =
    attributeType?.allowMany ||
    skuAttributes.every(
      (x) => x.organizationAttributeTypeId !== organizationAttributeType?.id
    );

  return (
    <Form
      onSubmit={onSubmit}
      validate={validate}
      initialValues={init}
      render={({ handleSubmit, submitting, submitError, values }) => (
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              {!organizationAttributeTypeProp && (
                <Field name="organizationAttributeTypeId" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectEntity
                          {...input}
                          labelField="displayName"
                          label={t({ id: "skuAttributeTypeSelectLabel" })}
                          required
                          fetchedData={fetchedOrganizationAttributeTypes}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                          parentChange={setInnerOrganizationAttributeType}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>
              )}

              {attributeType != null ? (
                <>
                  {canAddSkuAttribute ? (
                    <>
                      {attributeType?.isReusable ? (
                        // Select From the list
                        SkuAttributeFormComponent(
                          skuAttributes,
                          values,
                          organizationAttributeType!
                        )
                      ) : (
                        // Create a new one
                        <Field name="value">
                          {({ input, meta }) => TinyMCEComponent(input, meta)}
                        </Field>
                      )}
                      {attributeType?.codeConstant?.toLowerCase() ===
                        AttributeCodeConstants.Sponsors && (
                        <SponsorTypeField fieldName="sponsorTypeId" />
                      )}
                    </>
                  ) : (
                    <Alert severity="warning" sx={{ margin: "15px" }}>
                      <T id="attributeAlreadySelectedForSku" />
                    </Alert>
                  )}
                </>
              ) : (
                <Typography sx={{ margin: "15px" }}>
                  <T id="selectAttributeTypeMessage" />
                </Typography>
              )}

              {attributeType?.allowDates && (
                <>
                  <Grid item xs={12}>
                    <Field name="startDate">
                      {({ input, meta }) => (
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <DatePicker
                            label={t({ id: "startDateLabel" })}
                            format="MM/dd/yyyy"
                            disabled={false}
                            slotProps={{
                              textField: {
                                variant: "standard",
                                error: meta.error && meta.submitFailed,
                                helperText:
                                  meta.error && meta.submitFailed && meta.error,
                              },
                              actionBar: {
                                actions: ["clear", "cancel"],
                              },
                            }}
                            {...input}
                            value={input.value === "" ? null : input.value}
                          />
                        </FormControl>
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12}>
                    <Field name="endDate">
                      {({ input, meta }) => (
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <DatePicker
                            label={t({ id: "endDateLabel" })}
                            format="MM/dd/yyyy"
                            disabled={false}
                            slotProps={{
                              textField: {
                                variant: "standard",
                                error: meta.error && meta.submitFailed,
                                helperText:
                                  meta.error && meta.submitFailed && meta.error,
                              },
                              actionBar: {
                                actions: ["clear", "cancel"],
                              },
                            }}
                            {...input}
                            value={input.value === "" ? null : input.value}
                          />
                        </FormControl>
                      )}
                    </Field>
                  </Grid>
                </>
              )}

              {submitError && (
                <Alert sx={styles.feedback} severity="error">
                  {submitError}
                </Alert>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button disabled={submitting} onClick={onCancel}>
              {t({ id: "cancelButton" })}
            </Button>

            <Button
              type="submit"
              color="primary"
              disabled={submitting || (!values.attributeId && !values.value)}
              startIcon={submitting && <CircularProgress size={16} />}
            >
              {t({ id: "saveButton" })}
            </Button>
          </DialogActions>
        </form>
      )}
    />
  );
}
